<template>
	<div class="take-notes-page">
		<van-nav-bar class="custom-nav-bar" :title="$t('task.records_page_title')">
			<template #left>
				<div style="width: 86px; height: 32px; margin-left: 15px;" @click="back()">
					<div class="nav-bar__back">
						<span class="tn-icon-left-arrow"></span>
					</div>
				</div>
			</template>
		</van-nav-bar>
		<div class="page-content">
			<div class="tab-container">
				<div class="tab_list"></div>
			</div>
			<div class="list-wrap">
				<div v-if="historyLoading" class="loading-state">
					<div class="loading-text">{{ $t('common.loading') }}</div>
				</div>
				<div v-else-if="historyList.length" class="record">
					<div v-for="item in historyList" :key="item.id" class="record-item">
						<div class="record-header">
							<span class="record-name">{{ item.goods_name || '-' }}</span>
							<span class="record-status" :class="getStatusClass(item.status)">{{ item.status_text || getStatusText(item.status) }}</span>
						</div>
						<div class="record-body">
							<div class="record-row">
								<div class="record-label">{{ $t('task.contract_term') }}</div>
								<div class="record-value">{{ getDetailText(item) }}</div>
							</div>
							<div class="record-row">
								<div class="record-label">{{ $t('task.boost_amount') }}</div>
								<div class="record-value amount-text">{{ formatCurrency(item.amount) }}</div>
							</div>
							<div class="record-row">
								<div class="record-label">{{ $t('task.status_profit') }}</div>
								<div class="record-value profit-text">{{ formatProfit(item.profit) }}</div>
							</div>
							<div class="record-row">
								<div class="record-label">{{ $t('task.end_time') }}</div>
								<div class="record-value">{{ item.create_time_text || formatTime(item.create_time) }}</div>
							</div>
						</div>
					</div>
					<div class="pagination-bar">
						<div class="page-btn" :class="{ 'page-btn-dis': historyPage <= 1 || historyLoading }"
							@click="changeHistoryPage(-1)">
							<span class="page-btn-text">‹</span>
						</div>
						<div class="page-info">{{ historyPage }} / {{ historyTotalPages }}</div>
						<div class="page-btn" :class="{ 'page-btn-dis': historyPage >= historyTotalPages || historyLoading }"
							@click="changeHistoryPage(1)">
							<span class="page-btn-text">›</span>
						</div>
					</div>
				</div>
				<div v-else class="empty-state">
					<div class="empty-icon">
						<span class="tn-icon-empty-list tn-color-gray"></span>
					</div>
					<div class="empty-text">{{ $t('common.no_records') }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		data() {
			return {
				historyList: [],
				historyPage: 1,
				historyLimit: 10,
				historyTotalPages: 1,
				historyLoading: false
			};
		},
		mounted() {
			this.getTaskOrderHistory(1);
		},
		methods: {
			back() {
				return window.history.back();
			},
			async getTaskOrderHistory(page = this.historyPage) {
				this.historyLoading = true;
				try {
					const requestData = {
						page,
						limit: this.historyLimit
					};

					if (this.$route.query.productId) {
						requestData.goods_id = this.$route.query.productId;
					}

					const result = await this.$http({
						method: 'get',
						url: 'taskOrderHistory',
						data: requestData
					});

					if (!result || ![1, 200].includes(Number(result.code))) {
						this.historyList = [];
						this.historyTotalPages = 1;
						this.$toast((result && result.msg) || this.$t('task.order_record_load_failed'));
						return;
					}

					const payload = this.getHistoryPayload(result);
					this.historyList = Array.isArray(payload.data) ? payload.data : [];
					this.historyPage = Number(payload.current_page || page || 1);
					this.historyTotalPages = Math.max(1, Number(payload.total_pages || 1));
				} catch (error) {
					console.error('获取任务订单记录失败:', error);
					this.historyList = [];
					this.historyTotalPages = 1;
					this.$toast(this.$t('task.order_record_retry'));
				} finally {
					this.historyLoading = false;
				}
			},
			getHistoryPayload(result) {
				if (result && result.data2 && typeof result.data2 === 'object') {
					return result.data2;
				}

				if (result && result.data && typeof result.data === 'object' && !Array.isArray(result.data)) {
					return result.data;
				}

				return {};
			},
			changeHistoryPage(step) {
				if (this.historyLoading) {
					return;
				}

				const nextPage = this.historyPage + step;
				if (nextPage < 1 || nextPage > this.historyTotalPages) {
					return;
				}

				this.getTaskOrderHistory(nextPage);
			},
			getTicketLabel(ticketType) {
				return Number(ticketType) === 1 ? this.$t('task.ticket_open') : Number(ticketType) === 2 ? this.$t(
					'task.ticket_close') : '-';
			},
			getDetailText(item) {
				const ticketText = item.ticket_type_text || this.getTicketLabel(item.ticket_type);
				const contractName = item.contract_name || '-';
				const returnRateText = item.return_rate_text || (Number(item.return_rate) > 0 ?
					`+${Number(item.return_rate).toFixed(2)}%` : '-');
				return `${ticketText} ${contractName} · ${returnRateText}`;
			},
			getStatusText(status) {
				const normalized = Number(status);
				if (normalized === 1) {
					return this.$t('task.status_profit');
				}

				if (normalized === 2) {
					return this.$t('task.status_processed');
				}

				if (normalized === 3) {
					return this.$t('task.status_lost');
				}

				return this.$t('task.status_pending');
			},
			getStatusClass(status) {
				const normalized = Number(status);
				if (normalized === 1) {
					return 'status-win';
				}

				if (normalized === 3) {
					return 'status-lose';
				}

				if (normalized === 2) {
					return 'status-refund';
				}

				return 'status-pending';
			},
			formatCurrency(value) {
				return `€${Number(value || 0).toFixed(2)}`;
			},
			formatProfit(value) {
				return `+€${Number(value || 0).toFixed(2)}`;
			},
			formatTime(timeStr) {
				if (!timeStr) {
					return '-';
				}

				if (typeof timeStr === 'string' && timeStr.includes('-')) {
					return timeStr;
				}

				if (typeof timeStr === 'number') {
					const date = new Date(timeStr * 1000);
					const year = date.getFullYear();
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const day = String(date.getDate()).padStart(2, '0');
					const hours = String(date.getHours()).padStart(2, '0');
					const minutes = String(date.getMinutes()).padStart(2, '0');
					const seconds = String(date.getSeconds()).padStart(2, '0');
					return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
				}

				return String(timeStr);
			}
		}
	}
</script>

<style lang='less' scoped>
	.take-notes-page {
		background: #f5f5f5;
		min-height: 100vh
	}

	.page-content {
		padding-bottom: 20px
	}

	/* 导航栏返回按钮 */
	.custom-nav-bar {
		height: 45px;
		position: fixed;
		z-index: 999;
		display: block;
		position: relative;
		line-height: normal;
	}

	::v-deep .van-nav-bar__content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 0;
		height: 45px;
		padding-top: 0px;
		background-color: rgb(255, 255, 255);
		z-index: 29090;
		box-shadow: 0px 0px 41px 0px rgba(0, 0, 0, .05);
		position: fixed;
		width: 100%;
		top: 0;
	}

	::v-deep .van-nav-bar__left {
		padding: 0;
	}

	::v-deep .van-nav-bar__title {
		color: #000 !important;
		font-size: 18px;
		font-weight: 700;
		top: 0px;
		height: 45px;
		line-height: 45px;
		width: calc(100% - 100px);
		position: absolute;
		text-align: center;
		left: 0;
		right: 0;
		bottom: 0;
		cursor: none;
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
	}

	.nav-bar__back {
		width: 31px;
		height: 31px;
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		box-sizing: border-box;
		background-color: rgba(0, 0, 0, .05);
		border-radius: 50%;
		color: #333;
		font-size: 14px;
	}

	/* Tab */
	.tab-container {
		background: #fff;
		border-radius: 8px;
		margin: 0 10px 10px;
		box-shadow: 0 1px 6px rgba(0, 0, 0, .08);
		overflow: hidden
	}

	.tab_list {
		display: flex;
		align-items: center
	}

	.tab_list .tab_li {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		color: #333;
		padding: 12px 0
	}

	.tab_list .active_s {
		color: #667eea;
		border-bottom: 1px solid #667eea;
		font-weight: 700
	}

	/* 列表 */
	.list-wrap {
		padding: 0 10px
	}

	.record-item {
		background: #fff;
		border-radius: 8px;
		margin-bottom: 10px;
		overflow: hidden;
		box-shadow: 0 1px 6px rgba(0, 0, 0, .06)
	}

	.record-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 15px;
		border-bottom: 0.5px solid #f5f5f5
	}

	.record-header .record-name {
		font-size: 14px;
		font-weight: 700;
		color: #333;
		flex: 1
	}

	.record-status {
		padding: 3px 10px;
		border-radius: 10px;
		font-size: 11px;
		font-weight: 600;
		flex-shrink: 0
	}

	.record-status.status-pending {
		background: #fff3e0;
		color: #fea626
	}

	.record-status.status-win {
		background: #e6f7ef;
		color: #19cf8a
	}

	.record-status.status-lose {
		background: #fef0f0;
		color: #f5576c
	}

	.record-status.status-refund {
		background: #f0f0f0;
		color: #999
	}

	.record-body {
		padding: 10px 15px
	}

	.record-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 5px 0
	}

	.record-row .record-label {
		font-size: 12px;
		color: #999
	}

	.record-row .record-value {
		font-size: 13px;
		color: #333
	}

	.record-row .amount-text {
		font-weight: 600;
		color: #667eea
	}

	.record-row .profit-text {
		font-weight: 700;
		color: #19cf8a
	}

	/* 空状态 */
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 52px 0
	}

	.empty-state .empty-icon {
		font-size: 62px;
		margin-bottom: 12px;
	}

	.tn-color-gray {
		color: #f8f7f8 !important;
	}

	.empty-state .empty-text {
		font-size: 13px;
		color: #ccc;
		margin-top: 10px
	}

	.loading-state {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 52px 0
	}

	.loading-text {
		font-size: 13px;
		color: #bbb
	}

	/* 分页条 */
	.pagination-bar {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 15px 0 5px
	}

	.pagination-bar .page-btn {
		width: 33px;
		height: 33px;
		border-radius: 50%;
		background: #667eea;
		display: flex;
		align-items: center;
		justify-content: center
	}

	.pagination-bar .page-btn .page-btn-text {
		font-size: 18px;
		color: #fff;
		line-height: 1
	}

	.pagination-bar .page-btn.page-btn-dis {
		background: #ddd
	}

	.pagination-bar .page-btn.page-btn-dis .page-btn-text {
		color: #aaa
	}

	.pagination-bar .page-info {
		font-size: 14px;
		color: #667eea;
		font-weight: 600;
		margin: 0 16px
	}
</style>
